/**
 * Centralized auth helpers.
 *
 * AUTH DESIGN:
 *   Primary mechanism  → httpOnly Secure cookie set by the backend (`auth_token`).
 *   Fallback mechanism → Bearer token in Authorization header.
 *
 * localStorage / sessionStorage hold the token ONLY as a fallback for the
 * Authorization header.  The httpOnly cookie (inaccessible to JS) is the
 * primary credential sent with every `credentials: 'include'` request.
 *
 * XSS mitigation is handled via:
 *   1. httpOnly + Secure + SameSite=Lax cookie (cannot be read by JS)
 *   2. CORS origin validation on the backend
 *   3. Input sanitisation and React's built-in XSS escaping
 */

const STORAGE_KEYS = { token: 'token', user: 'user' };

export const getAuthToken = () =>
  localStorage.getItem(STORAGE_KEYS.token) || sessionStorage.getItem(STORAGE_KEYS.token);

export const getStoredUser = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.user) || sessionStorage.getItem(STORAGE_KEYS.user);
  return raw ? JSON.parse(raw) : null;
};

export const storeAuth = (token, user, keepLoggedIn = true) => {
  const storage = keepLoggedIn ? localStorage : sessionStorage;
  storage.setItem(STORAGE_KEYS.token, token);
  storage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem(STORAGE_KEYS.token);
  localStorage.removeItem(STORAGE_KEYS.user);
  sessionStorage.removeItem(STORAGE_KEYS.token);
  sessionStorage.removeItem(STORAGE_KEYS.user);
};

export const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Wrapper around fetch that includes credentials + auth header.
 */
export const authFetch = (url, opts = {}) =>
  fetch(url, {
    ...opts,
    credentials: 'include',
    headers: { ...opts.headers, ...authHeaders() },
  });
